<template>
      <section class="split-hero">

    <!-- ============ Split body ============ -->
    <div class="split-hero__body">
    <!-- Left: dark panel, primary copy -->
<div class="split-hero__panel split-hero__panel--left">
  <div class="split-hero__panel-inner">
    <span class="split-tag split-anim split-anim--1">
      <span class="split-tag-dot"></span>
      The B2B Sourcing Hub
    </span>

    <h1 class="split-title split-anim split-anim--2">
      Where Suppliers,<br />
      Manufacturers &amp;<br />
      Service Providers<br />
      Meet
    </h1>

    <p class="split-desc split-anim split-anim--3">
      One verified network for sourcing, compliance, and collaboration 
      built to help businesses find each other and grow together.
    </p>
  </div>

  <!-- Decorative accent bar replacing the social row  reuses the same accent color -->
  <div class="split-accent-row split-anim split-anim--4">
    <span class="split-accent-dot"></span>
    <span class="split-accent-dot"></span>
    <span class="split-accent-dot"></span>
    <span class="split-accent-line"></span>
  </div>
</div>

 <!-- Center: full-size hub image -->
<div class="split-hero__media split-anim split-anim--3" aria-hidden="true">
  <div class="split-hero__media-frame">
    <img
      class="split-hero__media-img"
      src="@/assets/media/hero.webp"
      alt="Business professional"
      onerror="this.style.display='none'"
    />
  </div>
  <div class="split-hero__media-glow"></div>
</div>
      <!-- Right: colored panel, stat + supporting copy -->
      <div class="split-hero__panel split-hero__panel--right">
        <div class="split-hero__panel-inner split-hero__panel-inner--right">
          <div class="split-index split-anim split-anim--2">
            12.5k<span class="split-index__suffix">+</span>
          </div>
          <p class="split-right-label split-anim split-anim--3">Verified members on the network</p>

          <p class="split-right-desc split-anim split-anim--4">
            Every profile is reviewed for compliance and credentials, so
            connections are built on trust  not guesswork.
          </p>

          <a href="#features" class="split-learn-more split-anim split-anim--5">
            Learn More
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>

        <span class="split-copyright">© {{ currentYear }}</span>
      </div>
    </div>
  </section>
  <!-- ============ How the hub works ============ -->
  <section class="how-it-works">
    <div class="section-inner">
      <span class="section-eyebrow">
        <span class="eyebrow-dot"></span>
        How It Works
      </span>
      <h2 class="section-title">Where sourcing gets simple</h2>
      <p class="section-subtitle">
        One network, three roles, endless connections. Here's how members find each other and get to work.
      </p>

      <div class="steps-row">
        <div class="step-card">
          <span class="step-number">01</span>
          <h3 class="step-title">Create your profile</h3>
          <p class="step-desc">Register as a supplier, manufacturer, or service provider and tell the network what you offer.</p>
        </div>
        <div class="step-connector">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </div>
        <div class="step-card">
          <span class="step-number">02</span>
          <h3 class="step-title">Get discovered</h3>
          <p class="step-desc">Verified members search the network by material, service, or region to find the right fit.</p>
        </div>
        <div class="step-connector">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </div>
        <div class="step-card">
          <span class="step-number">03</span>
          <h3 class="step-title">Connect & collaborate</h3>
          <p class="step-desc">Message directly, request connections, and build relationships that grow your business.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ Feature highlights ============ -->
  <section class="features">
    <div class="section-inner">
      <span class="section-eyebrow">
        <span class="eyebrow-dot"></span>
        Platform Highlights
      </span>
      <h2 class="section-title">Everything the network needs, built in</h2>
      <p class="section-subtitle">
        From verified profiles to real-time messaging, every tool is designed to help members find and trust each other faster.
      </p>

      <div class="features-grid">
        <div class="feature-card" v-for="feature in features" :key="feature.title">
          <span class="feature-icon" v-html="feature.icon"></span>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-desc">{{ feature.desc }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ Product showcase / media glimpse ============ -->
  <section class="showcase">
    <div class="section-inner">
      <div class="showcase-row showcase-row--reverse">
        <div class="showcase-media">
 <img
      src="@/assets/media/connect.webp"
      alt="Business professional"
      onerror="this.style.display='none'" style="
    border-radius: 15px;"
    />
        </div>
        <div class="showcase-copy">
          <span class="section-eyebrow section-eyebrow--left">
            <span class="eyebrow-dot"></span>
            For Buyers & Manufacturers
          </span>
          <h2 class="showcase-title">Source smarter with a network you can verify</h2>
          <p class="showcase-desc">
            Browse verified supplier profiles complete with materials, certifications, and compliance history 
            no more chasing down documents over email.
          </p>
          <ul class="showcase-list">
            <li><span class="check-dot">✓</span> Full material & product catalogs</li>
            <li><span class="check-dot">✓</span> Food safety & compliance certificates on file</li>
            <li><span class="check-dot">✓</span> Direct messaging with every connection</li>
          </ul>
        </div>
      </div>

      <div class="showcase-row">
        <div class="showcase-media">
           <img
      src="@/assets/media/products.webp"
      alt="Business professional"
      onerror="this.style.display='none'" style="
    border-radius: 15px;"
    />
        </div>
        <div class="showcase-copy">
          <span class="section-eyebrow section-eyebrow--left">
            <span class="eyebrow-dot"></span>
            For Suppliers & Service Providers
          </span>
          <h2 class="showcase-title">Get discovered by the right buyers</h2>
          <p class="showcase-desc">
            Build a complete profile once, then let the network bring qualified connections to you 
            complete with review scores and approval status buyers can trust.
          </p>
          <ul class="showcase-list">
            <li><span class="check-dot">✓</span> Ratings & reviews build your reputation</li>
            <li><span class="check-dot">✓</span> Approval badges signal you're verified</li>
            <li><span class="check-dot">✓</span> Track requests and connections in one place</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ Final CTA ============ -->
  <section class="cta-banner">
    <div class="section-inner cta-inner">
      <h2 class="cta-title">Ready to find your next connection?</h2>
      <p class="cta-desc">Join thousands of suppliers, manufacturers, and service providers already growing on the network.</p>
      <div class="cta-actions">
        <router-link :to="{ name: 'Signup' }" class="btn-primary btn-primary--light">Get Started Free</router-link>
        <router-link :to="{ name: 'SearchNetwork' }" class="btn-secondary btn-secondary--ghost">Browse the Network</router-link>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HomeHighlights',

  data() {
    return {
       currentYear: new Date().getFullYear(),
      features: [
        {
          title: 'Verified Profiles',
          desc: 'Every member goes through an approval process, so you know exactly who you\'re connecting with.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" /><path d="M9 12l2 2 4-4" /></svg>`,
        },
        {
          title: 'Materials & Products Catalog',
          desc: 'List every material and product you offer with packaging, lead times, and full specs in one place.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /></svg>`,
        },
        {
          title: 'Compliance & Certifications',
          desc: 'Food safety, IP certifications, and regulatory documents live on every profile, ready to review.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>`,
        },
        {
          title: 'Real-Time Messaging',
          desc: 'Chat directly with any connection, share files, and keep every conversation organized in one inbox.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.9-.94L3 20l1.06-3.9A8.4 8.4 0 0 1 3 11.5 8.5 8.5 0 0 1 20.5 8" /></svg>`,
        },
        {
          title: 'Reviews & Reputation',
          desc: 'Build trust over time with ratings and reviews from real connections across the network.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3 6 6.5.9-4.7 4.6 1.1 6.5L12 17l-5.9 3 1.1-6.5L2.5 8.9 9 8l3-6z" /></svg>`,
        },
        {
          title: 'Connection Requests',
          desc: 'Add trusted suppliers to your network and track approval status every step of the way.',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M2.5 20a6.5 6.5 0 0113 0" /><path d="M17 10l2 2 3.5-3.5" /></svg>`,
        },
      ],
    }
  },
  methods:{
      scrollToNext() {
      const next = this.$el.nextElementSibling
      if (next) next.scrollIntoView({ behavior: 'smooth' })
    },
  }
}
</script>

<style scoped>
/* ==========================================================================
   Shell
   ========================================================================== */
.split-hero {
  position: relative;
  width: 100%;
  min-height: 640px;
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}
.split-hero * {
  box-sizing: border-box;
}


/* ==========================================================================
   Split body
   ========================================================================== */
.split-hero__body {
  position: relative;
  display: flex;
  min-height: 640px;
}
.split-hero__panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 120px 56px 64px;
  position: relative;
}
@media (min-width: 901px) {
  .split-hero__panel--left {
    padding-right: calc(56px + 130px + 48px);
  }
  .split-hero__panel--right {
    padding-left: calc(56px + 130px + 48px);
  }
}
.split-hero__panel--left {
  background:
    radial-gradient(circle at 15% 15%, rgba(255, 255, 255, 0.05), transparent 45%),
    var(--color-primary);
}

.split-hero__panel--right {
  background: linear-gradient(160deg, var(--color-secondary), var(--color-primary) 130%);
}

.split-hero__panel-inner {
  max-width: 440px;
}
.split-hero__panel-inner--right {
  margin-left: auto;
  text-align: right;
  max-width: 380px;
}

/* Left panel copy */
.split-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin-bottom: 26px;
}
.split-tag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-tint-warm);
}

.split-title {
  margin: 0 0 20px;
  font-size: 42px;
  line-height: 1.16;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.8px;
}

.split-desc {
  margin: 0 0 32px;
  font-size: 14.5px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.65);
  max-width: 360px;
}

.split-cta-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.split-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 12px 22px;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.split-btn--primary {
  background: var(--color-tint-warm);
  color: var(--color-primary);
  border: none;
}
.split-btn--primary:hover {
  transform: translateY(-1px);
}
.split-btn--ghost {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
}
.split-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.5);
}

.split-social {
  display: flex;
  align-items: center;
  gap: 14px;
}
.split-social__link {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.18s ease, border-color 0.18s ease;
}
.split-social__link svg {
  width: 15px;
  height: 15px;
}
.split-social__link:hover {
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.5);
}

/* Right panel copy */
.split-index {
  font-size: 40px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -1px;
  line-height: 1;
}
.split-index__suffix {
  color: var(--color-tint-warm);
}

.split-right-label {
  margin: 8px 0 20px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.split-right-desc {
  margin: 0 0 22px;
  font-size: 13.5px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.68);
}

.split-learn-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.split-learn-more svg {
  width: 14px;
  height: 14px;
  transition: transform 0.18s ease;
}
.split-learn-more:hover svg {
  transform: translateX(3px);
}

.split-copyright {
  align-self: flex-end;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.45);
}

/* ==========================================================================
   Central network graphic
   ========================================================================== */
.split-hero__graphic {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 340px;
  transform: translateX(-50%);
  z-index: 10;
  pointer-events: none;
}

.graphic-lines {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.pulse-dot {
  animation: pulseDot 2.4s ease-in-out infinite;
}
.pulse-dot--delay1 { animation-delay: 0.4s; }
.pulse-dot--delay2 { animation-delay: 0.8s; }

@keyframes pulseDot {
  0%, 100% { opacity: 1; r: 3.5; }
  50% { opacity: 0.4; r: 5.5; }
}

.network-card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 14px;
  padding: 12px 16px;
  box-shadow: 0 20px 40px -14px rgba(12, 18, 83, 0.35);
  pointer-events: auto;
  min-width: 168px;
}

.network-card__icon {
  font-size: 20px;
  flex-shrink: 0;
}
.network-card__title {
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}
.network-card__meta {
  display: block;
  font-size: 10.5px;
  color: var(--color-text-muted);
  margin-top: 1px;
}

.network-card--top {
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
}
.network-card--left {
  top: 180px;
  left: -10px;
}
.network-card--right {
  top: 180px;
  right: -10px;
}
.network-card--bottom {
  top: 300px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-tint-warm);
  box-shadow: 0 20px 40px -14px rgba(236, 153, 58, 0.5);
}
.network-card--bottom .network-card__title,
.network-card--bottom .network-card__meta {
  color: var(--color-primary);
}

/* ==========================================================================
   Scroll indicator
   ========================================================================== */
.split-scroll {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  background: transparent;
  border: none;
  cursor: pointer;
}

.split-scroll__ring {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scrollBounce 2.2s ease-in-out infinite;
}

.split-scroll__label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
}

@keyframes scrollBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* ==========================================================================
   Responsive
   ========================================================================== */
@media (max-width: 1100px) {
  .split-hero__graphic {
    width: 280px;
  }
  .network-card--left { left: -30px; }
  .network-card--right { right: -30px; }
  .split-title { font-size: 36px; }
}

@media (max-width: 900px) {
  .split-hero__nav {
    padding: 20px 24px;
  }
  .split-hero__nav-links {
    display: none;
  }

  .split-hero__body {
    flex-direction: column;
  }
  .split-hero__panel {
    padding: 100px 32px 48px;
  }
  .split-hero__panel-inner--right {
    margin-left: 0;
    text-align: left;
    max-width: 440px;
  }
  .split-copyright {
    align-self: flex-start;
  }

  .split-hero__graphic {
    position: relative;
    left: auto;
    top: auto;
    transform: none;
    width: 100%;
    height: 380px;
    margin: 0 auto;
  }
  .network-card--left { left: 8%; }
  .network-card--right { right: 8%; }
}

@media (max-width: 560px) {
  .split-title {
    font-size: 30px;
  }
  .split-desc {
    max-width: none;
  }
  .split-cta-row {
    flex-direction: column;
    align-items: stretch;
  }
  .split-hero__panel {
    padding: 90px 20px 40px;
  }
  .network-card {
    min-width: 140px;
    padding: 10px 12px;
  }
  .network-card__title {
    font-size: 11.5px;
  }
}
/* ==========================================================================
   Shared section shell
   ========================================================================== */
.section-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
}

.section-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: var(--color-tint-light);
  color: var(--color-primary);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin-bottom: 16px;
}
.section-eyebrow--left {
  margin-bottom: 14px;
}

.eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-tint-warm);
}

.section-title {
  margin: 0 0 12px;
  font-size: 32px;
  font-weight: 700;
  color: var(--color-primary);
  letter-spacing: -0.4px;
}

.section-subtitle {
  margin: 0 auto 48px;
  max-width: 560px;
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text-muted);
}

/* ==========================================================================
   1. How it works
   ========================================================================== */
.how-it-works {
  width: 100%;
  background: var(--color-surface);
  padding: 90px 0;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}
.how-it-works * { box-sizing: border-box; }

.steps-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  text-align: left;
}

.step-card {
  flex: 1;
  max-width: 280px;
  background: var(--color-tint-light);
  border-radius: 16px;
  padding: 26px 22px;
}

.step-number {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-tint-warm);
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.step-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.step-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-muted);
}

.step-connector {
  flex-shrink: 0;
  align-self: center;
  color: var(--color-border);
  margin-top: -20px;
}
.step-connector svg {
  width: 24px;
  height: 24px;
}

/* ==========================================================================
   2. Feature grid
   ========================================================================== */
.features {
  width: 100%;
  background: var(--color-tint-light);
  padding: 90px 0;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}
.features * { box-sizing: border-box; }

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  text-align: left;
}

.feature-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 26px 22px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 34px -18px rgba(12, 18, 83, 0.28);
  border-color: var(--color-tint-warm);
}

.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: var(--color-tint-light);
  color: var(--color-primary);
  margin-bottom: 16px;
}
.feature-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.feature-title {
  margin: 0 0 6px;
  font-size: 15.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.feature-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--color-text-muted);
}

/* ==========================================================================
   3. Showcase (media + copy alternating rows)
   ========================================================================== */
.showcase {
  width: 100%;
  background: var(--color-surface);
  padding: 90px 0;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}
.showcase * { box-sizing: border-box; }

.showcase-row {
  display: flex;
  align-items: center;
  gap: 56px;
  text-align: left;
  margin-bottom: 90px;
}
.showcase-row:last-child { margin-bottom: 0; }
.showcase-row--reverse { flex-direction: row-reverse; }

.showcase-media,
.showcase-copy {
  flex: 1;
  min-width: 0;
}

.media-placeholder {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 18px;
  background: var(--color-tint-light);
  border: 1.5px dashed var(--color-border);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--color-text-muted);
}
.media-placeholder__icon {
  font-size: 34px;
}
.media-placeholder__label {
  font-size: 12.5px;
  font-weight: 600;
}
.media-placeholder--avatar {
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  border-width: 1px;
}
.media-placeholder--avatar .media-placeholder__icon {
  font-size: 16px;
}

.showcase-title {
  margin: 0 0 12px;
  font-size: 26px;
  font-weight: 700;
  color: var(--color-primary);
  letter-spacing: -0.3px;
}

.showcase-desc {
  margin: 0 0 20px;
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--color-text-muted);
}

.showcase-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.showcase-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.check-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(26, 127, 75, 0.12);
  color: #1a7f4b;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}


/* ==========================================================================
   5. Final CTA banner
   ========================================================================== */
.cta-banner {
  width: 100%;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  padding: 80px 0;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}
.cta-banner * { box-sizing: border-box; }

.cta-inner {
  max-width: 640px;
}

.cta-title {
  margin: 0 0 12px;
  font-size: 30px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.4px;
}

.cta-desc {
  margin: 0 0 28px;
  font-size: 14.5px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
}

.cta-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.btn-primary--light {
  background: #ffffff;
  color: var(--color-primary);
  box-shadow: 0 10px 24px -8px rgba(0, 0, 0, 0.25);
}
.btn-primary--light:hover {
  background: var(--color-tint-light);
}

.btn-secondary--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.35);
  color: #ffffff;
}
.btn-secondary--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  padding: 13px 26px;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.btn-primary:hover { transform: translateY(-1px); }
.btn-secondary { border: 1px solid var(--color-border); }

/* ==========================================================================
   6. Responsive
   ========================================================================== */
@media (max-width: 900px) {
  .steps-row { flex-direction: column; align-items: stretch; }
  .step-connector { display: none; }
  .step-card { max-width: none; }

  .features-grid { grid-template-columns: repeat(2, 1fr); }
  .testimonial-grid { grid-template-columns: repeat(2, 1fr); }

  .showcase-row,
  .showcase-row--reverse {
    flex-direction: column;
  }
}

@media (max-width: 620px) {
  .how-it-works,
  .features,
  .showcase,
  .testimonials,
  .cta-banner {
    padding: 64px 0;
  }

  .section-title { font-size: 26px; }
  .features-grid { grid-template-columns: 1fr; }
  .testimonial-grid { grid-template-columns: 1fr; }

  .cta-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
/* ==========================================================================
   Center media slot  inset, rounded, floating card treatment
   ========================================================================== */
.split-hero__media {
  position: absolute;
  left: 42%;
  top: 30px;
  bottom: 30px;
  width: 260px;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.split-hero__media-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 130px; /* large enough relative to width to fully round the sides */
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    0 40px 80px -24px rgba(12, 18, 83, 0.55),
    0 0 0 8px rgba(255, 255, 255, 0.04);
}

.split-hero__media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.split-hero__media-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(160deg, var(--color-secondary), var(--color-primary));
  color: rgba(255, 255, 255, 0.55);
}
.split-hero__media-icon {
  font-size: 36px;
}
.split-hero__media-label {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.split-hero__media-img[src]:not([src=""]) ~ .split-hero__media-placeholder {
  display: none;
}

/* Soft ambient glow behind the card  subtle, not neon */
.split-hero__media-glow {
  position: absolute;
  inset: -30px;
  z-index: -1;
  background: radial-gradient(circle at center, var(--color-tint-warm) 0%, transparent 65%);
  opacity: 0.16;
  filter: blur(40px);
  border-radius: 40px;
}
/* ==========================================================================
   Entrance animation  subtle fade + rise, staggered
   ========================================================================== */
.split-anim {
  opacity: 0;
  transform: translateY(14px);
  animation: splitFadeIn 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.split-anim--1 { animation-delay: 0.05s; }
.split-anim--2 { animation-delay: 0.16s; }
.split-anim--3 { animation-delay: 0.27s; }
.split-anim--4 { animation-delay: 0.38s; }
.split-anim--5 { animation-delay: 0.49s; }

@keyframes splitFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .split-anim {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
/* Remove .split-social / .split-social__link entirely, replace with: */
.split-accent-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.split-accent-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-tint-warm);
  opacity: 0.9;
}
.split-accent-dot:nth-child(2) { opacity: 0.6; }
.split-accent-dot:nth-child(3) { opacity: 0.35; }

.split-accent-line {
  width: 44px;
  height: 2px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.18);
  margin-left: 6px;
}
@media (max-width: 1100px) {
  .split-hero__media {
    width: 220px;
    top: 80px;
    bottom: 80px;
  }
  .split-hero__panel--left {
    padding-right: calc(48px + 110px + 32px);
  }
  .split-hero__panel--right {
    padding-left: calc(48px + 110px + 32px);
  }
  .split-title { font-size: 36px; }
}

@media (max-width: 900px) {
  .split-hero__panel--left,
  .split-hero__panel--right {
    padding-right: 32px;
    padding-left: 32px;
  }
  .split-hero__body {
    flex-direction: column;
  }
  .split-hero__panel {
    padding: 100px 32px 48px;
  }
  .split-hero__panel-inner--right {
    margin-left: 0;
    text-align: left;
    max-width: 440px;
  }
  .split-copyright {
    align-self: flex-start;
  }

  .split-hero__media {
    position: relative;
    left: auto;
    top: auto;
    bottom: auto;
    transform: none;
    width: 180px;
    height: 320px;
    margin: 32px auto;
  }
  .split-hero__media-frame {
    border-radius: 90px;
  }
}

@media (max-width: 560px) {
  .split-title {
    font-size: 30px;
  }
  .split-desc {
    max-width: none;
  }
  .split-hero__panel {
    padding: 90px 20px 40px;
  }
  .split-hero__media {
    width: 150px;
    height: 260px;
  }
  .split-hero__media-frame {
    border-radius: 75px;
  }
}
</style>